import AppDialog from '../../../../../components/common/AppDialog';
import AppButton from '../../../../../components/common/AppButton';
import {useSnackbar} from '../../../../../context/SnackbarContextProvider';
import {SVG} from '../../../../../constants/assetLists';
import {z} from 'zod';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import AppTextInput from '../../../../../components/common/AppTextInput';
import AppMUIAutocomplete from '../../../../../components/common/AppMUIAutoComplete';
import AppSwitch from '../../../../../components/common/AppSwitch';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useRolesContext} from '../../../../../context/users/RolesContextProvider';
import MUIIconCheckboxSelect from '../../../../../components/common/MUIIconCheckboxSelect';
import PermissionList from './PermissionList';
import {DepartmentOptionType} from '../../../../operations/components/Modals/AddEditCheckList';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import QUERY_KEYS from '../../../../../constants/queryKeys';
import {handleGetAllDepartmentsService} from '../../../../../services/operations/departments';
import {getAllPermissions, getCorrectPath} from '../../../../../utils';
import {ApiResponse} from '../../../../../services/axiosInstance';
import {
  handleCreateRoleService,
  handleGetSingleRolService,
  handleUpdateRoleService,
} from '../../../../../services/roles';
import {COLORS, ICONS} from '../../../data';
import {SingleRoleResponse} from '../roles';
import {handleGetAllRoleTypesService} from '../../../../../services/roleTypes';
import {handleGetPermissionsTreeService} from '../../../../../services/permissions';
import {Chip} from '@mui/material';
import {useClientsContext} from '../../../../../context/clients/ClientsContextProvider';
import {getRoleTypeName} from '../../../../../utils';

const zodSchema = z.object({
  isActive: z.boolean(),
  roleType: z.string({error: 'שדה חובה!'}).nonempty('שדה חובה!'),
  description: z.string().optional(),
  departments: z
    .array(z.string({error: 'שדה חובה!'}).nonempty('שדה חובה!'), {
      error: 'שדה חובה!',
    })
    .min(1, 'שדה חובה!'),
  icon: z.string({error: 'שדה חובה!'}).nonempty('שדה חובה!'),
  color: z.string({error: 'שדה חובה!'}).nonempty('שדה חובה!'),
  permissions: z
    .array(z.string('שדה הרשאה נדרש!').nonempty('שדה הרשאה נדרש!'), {
      error: 'שדה הרשאה נדרש!',
    })
    .min(1, 'נדרשת לפחות אישור אחד!'),
  clientIds: z
    .array(z.string('נדרש לקוח!').nonempty('נדרש לקוח!'))
    .min(1, 'נדרש לפחות לקוח אחד!'),
  branchIds: z.array(z.string('נדרש סניף!').nonempty('נדרש סניף!')).optional(),
});

export type RoleFormValues = z.infer<typeof zodSchema>;

export type RoleFormPayload = Omit<
  RoleFormValues,
  'isActive' | 'departments' | 'clientIds'
> & {
  departmentId: string[];
  clientId?: string;
};

function AddEditRoleDialog() {
  const {showSnackbar} = useSnackbar();
  const {selectedLocation, allClients} = useClientsContext();

  const {
    showAddEditDialog,
    setShowAddEditDialog,
    selectedRole,
    setSelectedRole,
  } = useRolesContext();

  const queryClient = useQueryClient();

  const [departmentOptions, setDepartmentOptions] = useState<
    DepartmentOptionType[]
  >([]);

  const method = useForm<RoleFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      isActive: true,
      roleType: '',
      description: '',
      departments: [],
      icon: '',
      color: '',
      permissions: [],
      clientIds: [],
      branchIds: [],
    },
    resolver: zodResolver(zodSchema),
  });

  const {
    formState: {errors, isSubmitting},
    reset,
    control,
    setValue,
    watch,
    getValues,
    handleSubmit,
  } = method;
  const departments = watch('departments');
  const permission = watch('permissions');
  const description = watch('description');
  const roleType = watch('roleType');
  const icon = watch('icon');
  const color = watch('color');
  const clientIds = watch('clientIds');
  const branchIds = watch('branchIds');

  const {data: allDepartments} = useQuery({
    queryKey: [QUERY_KEYS.GET_ALL_DEPARTMENTS, selectedLocation?.id],
    queryFn: () =>
      handleGetAllDepartmentsService({
        clientId: selectedLocation?.isClient
          ? selectedLocation?.id
          : selectedLocation?.clientId,
        branchId: !selectedLocation?.isClient
          ? selectedLocation?.id
          : undefined,
      }),
    enabled: showAddEditDialog && !!selectedLocation,
  });

  const {data: allRoleTypes} = useQuery({
    queryKey: [QUERY_KEYS.GET_ALL_ROLE_TYPES],
    queryFn: handleGetAllRoleTypesService,
    enabled: showAddEditDialog,
  });

  const {data: allGroupedPermissions} = useQuery({
    queryKey: [QUERY_KEYS.GET_PERMISSION_TREE],
    queryFn: handleGetPermissionsTreeService,
    enabled: showAddEditDialog,
  });

  const {data: userRoleData} = useQuery<
    ApiResponse<SingleRoleResponse>,
    Error,
    SingleRoleResponse
  >({
    queryKey: [QUERY_KEYS.GET_SINGLE_ROLE, selectedRole],
    queryFn: () => handleGetSingleRolService(selectedRole!),
    enabled: !!selectedRole && !!showAddEditDialog,
    staleTime: 0,
  });

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    (allClients ?? []).forEach((client: any) => {
      map.set(client.id, client.name);
      (client.branches ?? []).forEach((branch: any) => {
        map.set(branch.id, branch.name);
        (branch.subBranches ?? []).forEach((subBranch: any) => {
          map.set(subBranch.id, subBranch.name);
        });
      });
    });
    return map;
  }, [allClients]);

  const resolveLocationId = useCallback(
    (entries: any[] | undefined, preferredIds: string[]) => {
      const ids =
        entries
          ?.map(entry => (typeof entry === 'string' ? entry : entry?._id))
          .filter(Boolean) ?? [];
      return (
        ids.find(id => preferredIds.includes(id)) ??
        ids.find(id => locationNameById.has(id))
      );
    },
    [locationNameById],
  );

  const getDeptGroupLabel = useCallback(
    (department: any) => {
      const subBranchId = resolveLocationId(
        department.subBranchId,
        selectedLocation?.isSubBranch && selectedLocation.id
          ? [selectedLocation.id]
          : [],
      );
      if (subBranchId) return locationNameById.get(subBranchId) ?? '';

      const branchId = resolveLocationId(department.branchId, branchIds ?? []);
      if (branchId) return locationNameById.get(branchId) ?? '';

      const selectedClientId =
        selectedLocation && 'clientId' in selectedLocation
          ? selectedLocation.clientId
          : undefined;

      const clientId = resolveLocationId(
        department.clientId,
        clientIds?.length
          ? clientIds
          : selectedClientId
            ? [selectedClientId]
            : selectedLocation?.isClient && selectedLocation.id
              ? [selectedLocation.id]
              : [],
      );
      return clientId ? (locationNameById.get(clientId) ?? '') : '';
    },
    [
      branchIds,
      clientIds,
      locationNameById,
      resolveLocationId,
      selectedLocation,
    ],
  );

  const departmentsValue = useMemo(() => {
    if (!departmentOptions.length) return [];
    const selected = departments ?? [];
    const selectedIds =
      selected?.[0] === 'all' ? departmentOptions.map(o => o.value) : selected;

    return departmentOptions.filter(o => selectedIds.includes(o.value));
  }, [departments, departmentOptions]);

  useEffect(() => {
    if (showAddEditDialog && selectedRole && userRoleData?.data) {
      const role = userRoleData?.data;

      setValue('isActive', role?.status == 'active');
      setValue(
        'roleType',
        typeof role?.roleType === 'string'
          ? role?.roleType
          : role?.roleType?._id,
      );
      setValue('description', role?.description);
      setValue(
        'departments',
        role?.departmentId?.map((item: any) => item._id) || [],
      );
      setValue('icon', role?.icon);
      setValue('color', role?.color);
      setValue(
        'permissions',
        role?.permissions?.map((item: any) => item._id) || [],
      );

      // Set client and branch IDs from role data
      const roleClientIds = role?.clientIds || [];
      const roleBranchIds = role?.branchIds?.map((item: any) => item._id) || [];

      setValue('clientIds', roleClientIds);
      setValue('branchIds', roleBranchIds);
    }
  }, [showAddEditDialog, selectedRole, userRoleData]);

  // Auto-select client branches when creating new role
  useEffect(() => {
    // Only run for create mode (no selectedRole) when dialog opens
    if (!showAddEditDialog || selectedRole) return;

    // Get current values
    const currentClientIds = getValues().clientIds || [];
    const currentBranchIds = getValues().branchIds || [];

    // If already set, don't override
    if (currentClientIds.length > 0 || currentBranchIds.length > 0) return;

    // Auto-select based on selectedLocation
    if (selectedLocation) {
      let clientIdsToSelect: string[] = [];
      let branchIdsToSelect: string[] = [];

      if (selectedLocation.isClient) {
        // If client is selected, add client ID only (don't auto-select all branches)
        clientIdsToSelect = [selectedLocation.id];
      } else {
        // If branch is selected, add client ID + only this specific branch
        if (selectedLocation.clientId) {
          clientIdsToSelect = [selectedLocation.clientId];
          branchIdsToSelect = [selectedLocation.id];
        }
      }

      if (clientIdsToSelect.length > 0) {
        setValue('clientIds', clientIdsToSelect, {
          shouldValidate: true,
          shouldDirty: true,
        });
        if (branchIdsToSelect.length > 0) {
          setValue('branchIds', branchIdsToSelect, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }
    }
  }, [showAddEditDialog, selectedRole, selectedLocation]);

  useEffect(() => {
    if (selectedRole) return;

    const departments = getValues().departments;
    if (departments?.length === 1) {
      const selectedDepartment = allDepartments?.departments?.find(
        (item: any) => item.value === departments[0],
      );
      const selectedColor = COLORS.find(
        item => item.color === selectedDepartment?.color,
      );
      setValue('color', selectedColor?.color || '');
    } else {
      setValue('color', '');
    }
  }, [departments, selectedRole, allDepartments]);

  useEffect(() => {
    if (allDepartments && allDepartments?.departments?.length > 0) {
      const dept = [
        {name: 'כל המחלקות', _id: 'all', icon: SVG.saveAllIcon},
        ...allDepartments?.departments,
      ];
      setDepartmentOptions(
        dept
          ?.map(department => ({
            label: department.name,
            value: department._id,
            icon:
              department._id === 'all'
                ? department.icon
                : department.icon?.startsWith('icon')
                  ? ICONS.find(icon => icon.id === department.icon)?.icon || ''
                  : getCorrectPath(department.icon, 'image'),
            groupLabel:
              department._id === 'all' ? '' : getDeptGroupLabel(department),
          }))
          .sort((a, b) => a.groupLabel.localeCompare(b.groupLabel)),
      );
    }
  }, [allDepartments, getDeptGroupLabel]);

  const handleDepartmentsChange = (
    e: React.SyntheticEvent,
    value: {value: string}[] | null,
  ) => {
    const text = e.currentTarget.textContent;
    const allSelected = text === 'כל המחלקות';
    const departments = getValues().departments || [];

    const selectedValues = value?.map(item => item.value) || [];
    const allDeptSelected = allDepartments?.departments?.every((dept: any) =>
      selectedValues.includes(dept._id),
    );
    const hasAllInSelection = selectedValues.includes('all');
    const hadAllPreviously = departments.includes('all');

    let newValue: string[] = [];
    if (hadAllPreviously && !hasAllInSelection) {
      newValue = []; // reset
    } else if (hadAllPreviously) {
      newValue = selectedValues.filter(v => v !== 'all');
    } else if (allSelected || hasAllInSelection || allDeptSelected) {
      newValue = ['all'];
    } else {
      newValue = selectedValues;
    }

    setValue('departments', newValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleModalClose = () => {
    reset();
    setSelectedRole(null);
    setShowAddEditDialog(false);
  };

  const onSubmit = async (formData: RoleFormValues) => {
    const {isActive, departments, clientIds, ...rest} = formData;

    const payload = {
      ...rest,
      status: formData.isActive ? 'active' : 'inactive',
      departmentId: formData.departments?.includes('all')
        ? departmentOptions
            .filter(item => item.value !== 'all')
            ?.map(item => item.value)
        : formData.departments,
      clientIds: clientIds,
    };

    if (selectedRole) {
      handleUpdateRole({id: selectedRole, payload});
    } else {
      handleCreateRole(payload);
    }
  };

  const {mutate: handleCreateRole, isPending: isAdding} = useMutation({
    mutationFn: handleCreateRoleService,
    onSuccess: data => {
      showSnackbar({
        message: data?.message ?? 'רשימת פתיחת בר נוצרה',
        type: 'success',
      });
      handleModalClose();

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ROLES],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GROUPED_ROLES],
      });
    },
  });

  const {mutate: handleUpdateRole, isPending: isUpdating} = useMutation({
    mutationFn: ({id, payload}: {id: string; payload: any}) =>
      handleUpdateRoleService(id, payload),
    onSuccess: data => {
      showSnackbar({
        message: data?.message ?? 'רשימת פתיחת בר נוצרה',
        type: 'success',
      });
      handleModalClose();

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GROUPED_ROLES],
      });
    },
  });

  const allPermissions = getAllPermissions(allGroupedPermissions?.data || []);

  const permissionNames = useMemo(() => {
    if (allPermissions.length === 0) return [];
    return permission.map(
      permissionId =>
        allPermissions.find((perm: any) => perm._id === permissionId)
          ?.displayName.he || '',
    );
  }, [permission]);

  useEffect(() => {
    if (selectedRole && userRoleData?.data?.description && description) return;
    setValue('description', permissionNames.join(', '), {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [permissionNames]);

  const isDisabledButton =
    !description ||
    permission.length === 0 ||
    !!Object.keys(errors).length ||
    !roleType ||
    !icon ||
    !color;

  return (
    <AppDialog
      showModel={showAddEditDialog}
      title={selectedRole ? 'עריכת תפקיד' : 'הוספת תפקיד'}
      titleIcon={
        <img
          src={selectedRole ? SVG.outlinePencilDarkIcon : SVG.addGrayIcon}
          alt="icon"
          className="w-[14px] h-[14px] object-contain"
        />
      }
      onClose={handleModalClose}
      showHeaderLine={false}
      width={717}
      fullHeight>
      <div className="h-full flex flex-col gap-4 pt-4">
        <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto px-[40px] transition-all duration-300">
          <div className="flex flex-col gap-3">
            {selectedRole && (
              <div className="flex justify-end">
                <div className="flex items-center gap-2">
                  <Controller
                    name="isActive"
                    control={control}
                    render={({field: {onChange, value}}) => (
                      <AppSwitch onChange={onChange} checked={value} />
                    )}
                  />
                  <p
                    className={`m-0 ${watch('isActive') ? 'font-medium' : ''}`}>
                    תפקיד פעיל
                  </p>
                </div>
              </div>
            )}

            <Controller
              name="roleType"
              control={control}
              render={({field: {value}}) => {
                const selectedRoleTypeObj = allRoleTypes?.data?.find(
                  (item: any) => item?._id === value,
                );
                // If value matches an existing roleType id, show that object; otherwise show the typed string
                const autocompleteValue = selectedRoleTypeObj || value || '';
                const selectedIcon = selectedRoleTypeObj
                  ? ICONS.find(i => i.id === selectedRoleTypeObj.icon)?.icon
                  : undefined;

                return (
                  <div className="flex items-center">
                    <div className="w-10">
                      <img
                        src={SVG.roleDarkIcon}
                        className="w-[18px] h-[20px] object-contain"
                      />
                    </div>

                    <AppMUIAutocomplete
                      label="שם תפקיד"
                      size="small"
                      required
                      fullWidth
                      freeSolo
                      options={allRoleTypes?.data || []}
                      getOptionLabel={(option: any) => {
                        if (typeof option === 'string') return option;
                        return option?.name?.he || '';
                      }}
                      startAdornment={
                        selectedIcon ? (
                          <img
                            src={selectedIcon}
                            alt="icon"
                            className="w-[18px] h-[18px] object-contain ms-1"
                          />
                        ) : undefined
                      }
                      renderOption={(props, option: any) => {
                        if (typeof option === 'string') return null;
                        const {key, ...optionProps} = props;
                        return (
                          <li key={key} {...optionProps}>
                            <img
                              src={
                                ICONS.find(icon => icon.id === option.icon)
                                  ?.icon
                              }
                              alt="icon"
                              className="w-[18px] h-[18px] object-contain me-2"
                            />
                            {option?.name?.he}
                          </li>
                        );
                      }}
                      value={autocompleteValue}
                      onChange={(_e, val: any) => {
                        if (val && typeof val === 'object' && val?._id) {
                          // User selected an existing role type from the dropdown
                          setValue('roleType', val._id, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          setValue('icon', val?.icon, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else if (typeof val === 'string') {
                          // User typed a custom name
                          setValue('roleType', val, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else {
                          setValue('roleType', '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }}
                      onInputChange={(_e, newInputValue, reason) => {
                        if (reason === 'input') {
                          // Check if the typed text matches an existing roleType name
                          const matchingRoleType = allRoleTypes?.data?.find(
                            (item: any) => item?.name?.he === newInputValue,
                          );
                          if (matchingRoleType) {
                            setValue('roleType', matchingRoleType._id, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                            setValue('icon', matchingRoleType?.icon, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          } else {
                            // Store the typed string as the roleType value
                            setValue('roleType', newInputValue, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }
                        }
                      }}
                      error={!!errors.roleType}
                      helperText={errors.roleType?.message}
                    />
                  </div>
                );
              }}
            />

            <Controller
              name="description"
              control={control}
              render={({field}) => {
                return (
                  <div className="flex items-center">
                    <div className="w-10" />

                    <div className="w-full relative bg-[#1D1B2014] rounded-md">
                      <span
                        className={`text-[12px] font-medium text-[#49454F] absolute top-1 left-2 right-2`}>
                        תיאור
                      </span>
                      <AppTextInput
                        multiline
                        // label="תיאור"
                        placeholder="תיאור"
                        className="border-0 bg-transparent py-0 mb-0"
                        containerClassName={'mt-4 py-0 mb-0'}
                        maxLength={200}
                        {...field}
                        error={errors.description?.message || ''}
                      />
                    </div>
                  </div>
                );
              }}
            />

            <Controller
              name="departments"
              control={control}
              render={({field: {value: departments}}) => {
                return (
                  <div className="flex items-center">
                    <div className="w-10" />

                    <MUIIconCheckboxSelect
                      label="מחלקה/ות"
                      options={departmentOptions}
                      groupBy={option => option.groupLabel || ''}
                      size="small"
                      fullWidth
                      disableCloseOnSelect
                      required
                      id="departments-autocomplete"
                      renderValue={(selectedOptions, getTagProps) => {
                        const allSelected =
                          departments?.[0] === 'all' ||
                          selectedOptions?.length === departmentOptions.length;

                        const renderChip = (
                          option: (typeof departmentOptions)[0],
                          index = 0,
                        ) => (
                          <Chip
                            label={option.label}
                            icon={
                              <img
                                src={option.icon}
                                alt="icon"
                                className="w-[18px] h-[18px] object-contain"
                              />
                            }
                            {...getTagProps({index})}
                          />
                        );

                        if (allSelected) {
                          const allOption = departmentOptions.find(
                            o => o.value === 'all',
                          );
                          return allOption ? renderChip(allOption) : null;
                        }

                        return selectedOptions.map((option, index) =>
                          renderChip(option, index),
                        );
                      }}
                      value={departmentsValue}
                      onChange={handleDepartmentsChange}
                      error={!!errors.departments}
                      helperText={errors.departments?.message}
                    />
                  </div>
                );
              }}
            />

            <div className="flex items-start gap-3">
              <div className="w-10" />

              <Controller
                name="icon"
                control={control}
                render={({field: {value}}) => {
                  return (
                    <AppMUIAutocomplete
                      label="איקון"
                      size="small"
                      options={ICONS}
                      fullWidth
                      getOptionLabel={o => o.id}
                      slotProps={{
                        listbox: {
                          sx: {
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            maxHeight: 320,
                            overflowY: 'auto',
                            '& .MuiAutocomplete-option': {
                              p: '8px',
                            },
                          },
                        },
                      }}
                      renderValue={value =>
                        value && (
                          <div className="flex items-center gap-1">
                            <img
                              src={value?.icon}
                              alt="icon"
                              className="w-[20px] h-[20px] object-contain"
                            />
                            <p>איקון</p>
                          </div>
                        )
                      }
                      renderOption={(props, option) => {
                        const {key, ...optionProps} = props;

                        return (
                          <li key={key} {...optionProps}>
                            <img
                              src={option?.icon}
                              alt="icon"
                              className="w-[32px] h-[32px] object-contain"
                            />
                          </li>
                        );
                      }}
                      required
                      value={ICONS.find(item => item?.id === value) || null}
                      onChange={(_e, val) => {
                        if (val) {
                          setValue('icon', val?.id, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else {
                          setValue('icon', '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }}
                      error={!!errors.icon}
                      helperText={errors.icon?.message}
                    />
                  );
                }}
              />

              <Controller
                name="color"
                control={control}
                render={({field: {value}}) => {
                  return (
                    <AppMUIAutocomplete
                      label="צבע"
                      size="small"
                      options={COLORS}
                      fullWidth
                      getOptionLabel={o => o.color} // avoid empty label space
                      slotProps={{
                        listbox: {
                          sx: {
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            maxHeight: 320,
                            overflowY: 'auto',
                            '& .MuiAutocomplete-option': {
                              p: '8px',
                              '&.Mui-focused, &[aria-selected="true"]': {
                                backgroundColor: 'transparent !important',
                              },
                            },
                          },
                        },
                      }}
                      renderValue={value =>
                        value && (
                          <div
                            className="w-[24px] h-[24px] rounded-[3px] border-1 border-[#777777]"
                            style={{backgroundColor: value?.color}}
                          />
                        )
                      }
                      renderOption={(props, option) => {
                        const {key, ...optionProps} = props;
                        const isSelected = value === option?.color;

                        return (
                          <li key={key} {...optionProps}>
                            <div
                              className="w-[24px] h-[24px] rounded-[3px]"
                              style={{
                                backgroundColor: option?.color,
                                border: isSelected
                                  ? '2px solid #333333'
                                  : '1px solid #333333',
                              }}
                            />
                          </li>
                        );
                      }}
                      required
                      value={COLORS.find(item => item?.color === value) || null}
                      onChange={(_e, val) => {
                        if (val) {
                          setValue('color', val?.color, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else {
                          setValue('color', '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }}
                      error={!!errors.color}
                      helperText={errors.color?.message}
                    />
                  );
                }}
              />
            </div>
          </div>

          <FormProvider {...method}>
            <PermissionList allGroupedPermissions={allGroupedPermissions} />
          </FormProvider>
        </div>

        <div className="flex justify-end px-[48px]">
          <div className="flex items-center gap-2">
            <AppButton onClick={handleModalClose}>ביטול</AppButton>

            <AppButton
              variant="primary"
              isLoading={isSubmitting || isAdding || isUpdating}
              disabled={isDisabledButton}
              onClick={handleSubmit(onSubmit)}>
              שמירה
            </AppButton>
          </div>
        </div>
      </div>
    </AppDialog>
  );
}

export default AddEditRoleDialog;
